import { canonicalAssetLanguage, genreTags } from "./vod-title";
import { localisedByLanguage, localisedFor, localisedLanguages, localisedRows, } from "./vod-localised";
import { resolveVodThumbnailImage } from "./vod-images";
export function instantMs(value) {
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    if (typeof value !== "string" || value.trim() === "")
        return null;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}
export function isoInstant(value) {
    const ms = instantMs(value);
    return ms === null ? null : new Date(ms).toISOString();
}
export const GUIDE_GAP_MIN_SECONDS = 60;
export function resolveChannelEvents(events) {
    const invalid = [];
    const spans = [];
    for (const event of events) {
        const start = instantMs(event === null || event === void 0 ? void 0 : event.startScheduled);
        const end = instantMs(event === null || event === void 0 ? void 0 : event.endScheduled);
        if (start === null || end === null) {
            invalid.push({
                epgEventId: event === null || event === void 0 ? void 0 : event.epgEventId,
                channelAssetId: event === null || event === void 0 ? void 0 : event.channelAssetId,
                startScheduled: event === null || event === void 0 ? void 0 : event.startScheduled,
                endScheduled: event === null || event === void 0 ? void 0 : event.endScheduled,
                reason: "unreadable_instant",
            });
            continue;
        }
        if (end <= start) {
            invalid.push({
                epgEventId: event.epgEventId,
                channelAssetId: event.channelAssetId,
                startScheduled: event.startScheduled,
                endScheduled: event.endScheduled,
                reason: "not_after_start",
            });
            continue;
        }
        spans.push({ event, start, end });
    }
    const byPrecedence = [...spans].sort((a, b) => {
        var _a, _b, _c, _d;
        const aAt = (_a = instantMs(a.event.createdAt)) !== null && _a !== void 0 ? _a : 0;
        const bAt = (_b = instantMs(b.event.createdAt)) !== null && _b !== void 0 ? _b : 0;
        if (aAt !== bAt)
            return bAt - aAt;
        return ((_c = b.event.epgEventId) !== null && _c !== void 0 ? _c : 0) - ((_d = a.event.epgEventId) !== null && _d !== void 0 ? _d : 0);
    });
    const kept = [];
    const superseded = [];
    for (const candidate of byPrecedence) {
        const clash = kept.find((k) => candidate.start < k.end && candidate.end > k.start);
        if (clash) {
            superseded.push({
                event: candidate.event,
                supersededBy: clash.event.epgEventId,
            });
            continue;
        }
        kept.push(candidate);
    }
    kept.sort((a, b) => a.start - b.start || a.event.epgEventId - b.event.epgEventId);
    superseded.sort((a, b) => {
        var _a, _b;
        return ((_a = instantMs(a.event.startScheduled)) !== null && _a !== void 0 ? _a : 0) -
            ((_b = instantMs(b.event.startScheduled)) !== null && _b !== void 0 ? _b : 0);
    });
    return {
        events: kept.map((k) => k.event),
        superseded,
        invalid,
    };
}
function pickLocalisedTitleRow(asset, language) {
    const requestedCode = canonicalAssetLanguage(language);
    const byLanguage = localisedByLanguage(asset);
    if (requestedCode) {
        const row = byLanguage.get(requestedCode);
        const title = typeof (row === null || row === void 0 ? void 0 : row.title) === "string" ? row.title.trim() : "";
        if (title)
            return { title, code: requestedCode };
    }
    for (const code of localisedLanguages(asset)) {
        if (code === requestedCode)
            continue;
        const row = byLanguage.get(code);
        const title = typeof (row === null || row === void 0 ? void 0 : row.title) === "string" ? row.title.trim() : "";
        if (title)
            return { title, code };
    }
    return null;
}
function assetTitle(asset, language) {
    if (!asset)
        return { title: "", titleLanguage: null };
    const picked = pickLocalisedTitleRow(asset, language);
    if (picked)
        return { title: picked.title, titleLanguage: picked.code };
    const original = typeof asset.originalTitle === "string" ? asset.originalTitle.trim() : "";
    return { title: original, titleLanguage: null };
}
function assetSynopsis(asset, language) {
    if (!asset)
        return "";
    const picked = pickLocalisedTitleRow(asset, language);
    const row = (picked === null || picked === void 0 ? void 0 : picked.code)
        ? localisedFor(asset, picked.code)
        : localisedFor(asset, language);
    const short = typeof (row === null || row === void 0 ? void 0 : row.synopsisShort) === "string" ? row.synopsisShort : "";
    if (short.trim())
        return short.trim();
    const medium = typeof (row === null || row === void 0 ? void 0 : row.synopsisMedium) === "string" ? row.synopsisMedium : "";
    return medium.trim();
}
function guideImage(asset, language) {
    var _a;
    if (!asset)
        return null;
    const languages = localisedRows(asset)
        .map((row) => row === null || row === void 0 ? void 0 : row.language)
        .filter((code) => typeof code === "string");
    const image = resolveVodThumbnailImage({
        images: asset.images,
        language,
        languages,
    });
    if (!image || typeof image.url !== "string" || !image.url.trim())
        return null;
    return {
        url: image.url,
        type: image.type,
        language: (_a = canonicalAssetLanguage(image.language)) !== null && _a !== void 0 ? _a : image.language,
    };
}
export function toGuideEvent(event, language) {
    var _a;
    const start = isoInstant(event.startScheduled);
    const end = isoInstant(event.endScheduled);
    const { title, titleLanguage } = assetTitle(event.programAsset, language);
    return {
        epgEventId: event.epgEventId,
        externalId: event.externalId,
        channelAssetId: event.channelAssetId,
        programAssetId: event.programAssetId,
        startScheduled: start,
        endScheduled: end,
        durationSeconds: Math.round((Date.parse(end) - Date.parse(start)) / 1000),
        title,
        titleLanguage,
        synopsis: assetSynopsis(event.programAsset, language),
        image: guideImage(event.programAsset, language),
        ingestedAt: (_a = isoInstant(event.createdAt)) !== null && _a !== void 0 ? _a : start,
    };
}
function assetSynopsisLong(asset, language) {
    if (!asset)
        return "";
    const picked = pickLocalisedTitleRow(asset, language);
    const row = picked
        ? localisedFor(asset, picked.code)
        : localisedFor(asset, language);
    const long = typeof (row === null || row === void 0 ? void 0 : row.synopsisLong) === "string" ? row.synopsisLong.trim() : "";
    if (long)
        return long;
    const medium = typeof (row === null || row === void 0 ? void 0 : row.synopsisMedium) === "string" ? row.synopsisMedium.trim() : "";
    if (medium)
        return medium;
    const short = typeof (row === null || row === void 0 ? void 0 : row.synopsisShort) === "string" ? row.synopsisShort.trim() : "";
    return short;
}
function assetImages(asset) {
    var _a;
    if (!asset)
        return [];
    return ((_a = asset.images) !== null && _a !== void 0 ? _a : [])
        .filter((img) => typeof (img === null || img === void 0 ? void 0 : img.url) === "string" && img.url.trim() !== "")
        .map((img) => {
        var _a;
        return ({
            url: img.url,
            type: img.type,
            language: (_a = canonicalAssetLanguage(img.language)) !== null && _a !== void 0 ? _a : img.language,
        });
    });
}
export function toGuideEventDetail(event, language) {
    var _a, _b, _c, _d, _e, _f;
    const resolvedLanguage = (_a = canonicalAssetLanguage(language !== null && language !== void 0 ? language : GUIDE_DEFAULT_LANGUAGE)) !== null && _a !== void 0 ? _a : GUIDE_DEFAULT_LANGUAGE;
    const base = toGuideEvent(event, resolvedLanguage);
    const asset = event.programAsset;
    return {
        ...base,
        synopsisLong: assetSynopsisLong(asset, resolvedLanguage),
        images: assetImages(asset),
        genres: genreTags(asset !== null && asset !== void 0 ? asset : {}).map((tag) => tag.name),
        parentalRatings: (_b = asset === null || asset === void 0 ? void 0 : asset.parentalRatings) !== null && _b !== void 0 ? _b : [],
        provider: (_d = (_c = asset === null || asset === void 0 ? void 0 : asset.provider) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : "",
        productionYear: (asset === null || asset === void 0 ? void 0 : asset.productionYear) || null,
        releaseYear: (asset === null || asset === void 0 ? void 0 : asset.releaseYear) || null,
        countryOfOrigin: (asset === null || asset === void 0 ? void 0 : asset.countryOfOrigin) || null,
        seasonNumber: (_e = asset === null || asset === void 0 ? void 0 : asset.seasonNumber) !== null && _e !== void 0 ? _e : null,
        episodeNumber: (_f = asset === null || asset === void 0 ? void 0 : asset.episodeNumber) !== null && _f !== void 0 ? _f : null,
    };
}
function gapsIn(events, from, to) {
    const gaps = [];
    let cursor = from;
    for (const event of events) {
        const start = Date.parse(event.startScheduled);
        const end = Date.parse(event.endScheduled);
        if (start > cursor) {
            const seconds = Math.round((Math.min(start, to) - cursor) / 1000);
            if (seconds >= GUIDE_GAP_MIN_SECONDS) {
                gaps.push({
                    from: new Date(cursor).toISOString(),
                    to: new Date(Math.min(start, to)).toISOString(),
                    seconds,
                });
            }
        }
        cursor = Math.max(cursor, end);
        if (cursor >= to)
            break;
    }
    if (cursor < to) {
        const seconds = Math.round((to - cursor) / 1000);
        if (seconds >= GUIDE_GAP_MIN_SECONDS) {
            gaps.push({
                from: new Date(cursor).toISOString(),
                to: new Date(to).toISOString(),
                seconds,
            });
        }
    }
    return gaps;
}
export const GUIDE_DEFAULT_LANGUAGE = "en";
export function resolveGuide(events, input) {
    var _a, _b, _c, _d, _e, _f, _g;
    const language = (_b = canonicalAssetLanguage((_a = input.language) !== null && _a !== void 0 ? _a : GUIDE_DEFAULT_LANGUAGE)) !== null && _b !== void 0 ? _b : GUIDE_DEFAULT_LANGUAGE;
    const from = (_c = instantMs(input.from)) !== null && _c !== void 0 ? _c : 0;
    const to = (_d = instantMs(input.to)) !== null && _d !== void 0 ? _d : from;
    const asked = input.channelAssetIds.map(String);
    const wanted = new Set(asked);
    const byChannel = new Map();
    const foreign = new Set();
    for (const event of events) {
        const id = String((_e = event === null || event === void 0 ? void 0 : event.channelAssetId) !== null && _e !== void 0 ? _e : "");
        if (!wanted.has(id)) {
            if (id)
                foreign.add(id);
            continue;
        }
        const list = byChannel.get(id);
        if (list)
            list.push(event);
        else
            byChannel.set(id, [event]);
    }
    const channels = asked.map((channelAssetId) => {
        var _a;
        const raw = (_a = byChannel.get(channelAssetId)) !== null && _a !== void 0 ? _a : [];
        const resolved = resolveChannelEvents(raw);
        const guideEvents = resolved.events.map((event) => toGuideEvent(event, language));
        const withChannel = raw.find((event) => event.channelAsset);
        return {
            channelAssetId,
            channelTitle: withChannel
                ? assetTitle(withChannel.channelAsset, language).title || null
                : null,
            events: guideEvents,
            superseded: resolved.superseded.map(({ event, supersededBy }) => ({
                ...toGuideEvent(event, language),
                supersededBy,
            })),
            invalid: resolved.invalid,
            gaps: gapsIn(guideEvents, from, to),
        };
    });
    return {
        window: {
            from: (_f = isoInstant(input.from)) !== null && _f !== void 0 ? _f : new Date(from).toISOString(),
            to: (_g = isoInstant(input.to)) !== null && _g !== void 0 ? _g : new Date(to).toISOString(),
        },
        language,
        channels,
        foreignChannelAssetIds: [...foreign].sort(),
    };
}
export function eventAt(events, instant) {
    const at = instant instanceof Date ? instant.getTime() : instantMs(instant);
    if (at === null)
        return null;
    for (const event of events) {
        const start = instantMs(event.startScheduled);
        const end = instantMs(event.endScheduled);
        if (start === null || end === null)
            continue;
        if (start <= at && at < end)
            return event;
    }
    return null;
}
